import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { OrderReturnDetailProps } from 'components/LightConversation/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PackageTabs } from '../PackageTabs';
import { OrderReturnDetailContent } from './OrderReturnDetailContent';
import { OrderReturnDetailContentSkeleton } from './OrderReturnDetailContentSkeleton';
import { OrderDrawerDetailBody } from '../OrderDrawer/DetailBody';
import { LazadaOrderReturnFooter } from './OrderReturnFooter';
import { LazadaOrderReturnDetailProvider } from './context';
import { useLazadaReturnOps } from 'hooks/business/chat/useLazadaReturnOps';
import operateBtns from 'components/Refund/OperateBtns';
import { toUpperCase } from 'utils/toLowerCase';
import { ReturnOrderListItemV2 } from 'services/api/refund';
import { Contact } from '../Contact';

export const OrderReturnDetail = (props: OrderReturnDetailProps) => {
  const { order, contactEnable = false } = props;
  const { externalTradeId, platform } = order;
  const { isLoading, data, refetch } = useQuery({
    ...queries.orders.returnOrderRelated({
      orderSn: externalTradeId,
      platform: platform,
      pageSize: 10,
    }),
  });
  const returnOrderDataList = data?.returnOrderDataList;

  const [activeTab, setActiveTab] = useState(returnOrderDataList?.[0]?.returnSn || '');

  const options = useMemo(
    () =>
      returnOrderDataList?.map((item) => ({
        value: item.returnSn,
        label: item.returnSn,
      })) || [],
    [returnOrderDataList],
  );

  const activeReturnOrder = useMemo(() => {
    return returnOrderDataList?.find((item) => item.returnSn === activeTab);
  }, [returnOrderDataList, activeTab]);

  const lazadaReturnOps = useLazadaReturnOps(activeReturnOrder?.returnItemData || [], platform);

  useEffect(() => {
    if (returnOrderDataList && returnOrderDataList[0]) {
      setActiveTab(returnOrderDataList[0].returnSn);
    }
  }, [returnOrderDataList]);

  const renderCommonFooter = useCallback(
    (returnOrder: ReturnOrderListItemV2) => {
      const {
        operate,
        internalStoreId,
        unit,
        refundAmount,
        latestOfferAmount,
        offerCounterLimit,
        operationLog = [],
        externalStoreId,
        allowOffer,
        displayStatus,
        internalTradeId,
        preDispute,
        region,
        returnSn,
        buyerNick,
        externalBuyerId,
      } = returnOrder;
      const btns = operateBtns({
        externalStoreId,
        externalTradeId,
        returnSn,
        operate,
        refreshList: refetch,
        internalStoreId,
        internalTradeId: `${internalTradeId}`,
        displayStatus,
        unit,
        refundAmount,
        latestOfferAmount,
        allowOffer,
        offerCounterLimit,
        operationLog,
        platform: toUpperCase(platform),
        preDispute,
        region: toUpperCase(region),
        size: 'middle',
        type: 'primary',
      });
      const hasContent = btns.length > 0 || contactEnable;
      if (hasContent) {
        return (
          <div className="flex items-center gap-4">
            {btns}
            {contactEnable ? (
              <Contact
                platform={toUpperCase(platform)}
                orderSn={externalTradeId}
                region={toUpperCase(region) as General.UpperCaseRegion}
                externalBuyerId={externalBuyerId}
                externalStoreId={externalStoreId}
                buyerNick={buyerNick}
                returnSn={returnSn}
                type="refund"
                onClose={refetch}
              />
            ) : null}
          </div>
        );
      }
    },
    [contactEnable, externalTradeId, platform, refetch],
  );

  const footerProps = activeReturnOrder
    ? {
        returnOrder: activeReturnOrder,
        externalTradeId,
        platform,
        onUpdate: refetch,
        lazadaReturnOps,
      }
    : null;
  let footer = null;
  if (footerProps) {
    if (platform === 'lazada') {
      footer = lazadaReturnOps.hasOpsBtn ? <LazadaOrderReturnFooter {...footerProps} /> : null;
    } else if (footerProps.returnOrder) {
      const commonFooter = renderCommonFooter(footerProps.returnOrder);
      if (commonFooter) {
        footer = commonFooter;
      }
    }
  }

  return (
    <LazadaOrderReturnDetailProvider>
      <OrderDrawerDetailBody footer={footer}>
        {isLoading ? (
          <OrderReturnDetailContentSkeleton />
        ) : (
          <>
            {options.length > 1 && (
              <PackageTabs
                value={activeTab}
                options={options}
                onChange={(value) => setActiveTab(value as string)}
                className="mb-4"
              />
            )}
            {activeReturnOrder && (
              <OrderReturnDetailContent
                {...props}
                returnOrder={activeReturnOrder}
                lazadaReturnOps={lazadaReturnOps}
              />
            )}
          </>
        )}
      </OrderDrawerDetailBody>
    </LazadaOrderReturnDetailProvider>
  );
};
